import { DateTime } from 'luxon';
import { sortByApiParam } from 'src/app/util/sorting';
import { Approval, ApprovalStatus } from 'src/models';
import { GetApprovalsOptions } from '../approvals/approvals.service';
import { GetWorkSchedulesParams, LeaveType, Shift } from '../schedules';
import { DEMO_REPORT_TASKS, DEMO_REPORT_PROJECTS } from './demo-projects-report.mock';
import { buildDemoReportUsers, filterDemoReportUsers } from './demo-report-users.mock';
import { DemoSeedService } from './demo-seed.service';

const DEMO_REASONS = [
  'Forgot to start the timer',
  'Client meeting off-site',
  'Worked from phone',
  'System crash — re-entering time',
  'Training session',
  'Internet outage',
] as const;

const DEMO_LEAVE_REASONS = [
  'Personal day',
  'Medical appointment',
  'Family commitment',
  'Vacation',
] as const;

const REVIEWER = { id: 'demo-reviewer-1', name: 'Alex Manager' };

export type BuildMockApprovalsOptions = GetApprovalsOptions & {
  user?: string | string[];
  tag?: string | string[];
  approved?: string;
  timezone?: string;
};

function resolveTimezone(options: { timezone?: string }): string {
  return options.timezone || 'UTC';
}

/** Inclusive local calendar-day bounds for API `from` / exclusive `to` instants. */
function resolveDateRange(options: { from?: string, to?: string, timezone?: string }) {
  const zone = resolveTimezone(options);
  const fallbackFrom = DateTime.now().setZone(zone).minus({ days: 30 }).startOf('day');
  const fallbackToExclusive = DateTime.now().setZone(zone).plus({ days: 1 }).startOf('day');
  const fromDay = DateTime.fromISO(options.from || fallbackFrom.toUTC().toISO(), { zone }).startOf('day');
  const toExclusive = DateTime.fromISO(options.to || fallbackToExclusive.toUTC().toISO(), { zone });
  const lastDay = toExclusive.minus({ millisecond: 1 }).startOf('day');
  return { fromDay, lastDay, zone };
}

function eachWeekday(from: DateTime, to: DateTime, fn: (day: DateTime, index: number) => void): void {
  let day = from.startOf('day');
  const end = to.startOf('day');
  let index = 0;
  while (day <= end) {
    if (day.weekday <= 5) {
      fn(day, index);
      index += 1;
    }
    day = day.plus({ days: 1 });
  }
}

function statusForRoll(roll: number): ApprovalStatus {
  if (roll < 55) {
    return 'pending';
  }
  if (roll < 85) {
    return 'approved';
  }
  return 'disapproved';
}

function matchesApprovedFilter(status: ApprovalStatus, approved?: string): boolean {
  if (!approved || approved === 'all') {
    return true;
  }
  return status === approved;
}

function buildManualApproval(
  seed: DemoSeedService,
  userId: string,
  userName: string,
  day: DateTime,
  dayIndex: number,
  slot: number,
): Approval {
  const project = DEMO_REPORT_PROJECTS[
    seed.pickIndex(DEMO_REPORT_PROJECTS.length, 'approval-project', userId, dayIndex, slot)
  ];
  const tasks = DEMO_REPORT_TASKS.filter(item => item.projectId === project.id);
  const task = tasks[
    seed.pickIndex(Math.max(tasks.length, 1), 'approval-task', userId, dayIndex, slot)
  ] || DEMO_REPORT_TASKS[0];

  const startHour = 9 + seed.int(0, 6, 'approval-hour', userId, dayIndex, slot);
  const durationHours = 1 + seed.int(0, 2, 'approval-hours', userId, dayIndex, slot);
  const start = day.set({ hour: startHour, minute: 0, second: 0, millisecond: 0 });
  const end = start.plus({ hours: durationHours });
  const createdAt = start.minus({ hours: 2 + seed.int(0, 20, 'approval-created', userId, dayIndex, slot) });
  const status = statusForRoll(seed.int(0, 99, 'approval-status', userId, dayIndex, slot));
  const reason = DEMO_REASONS[
    seed.pickIndex(DEMO_REASONS.length, 'approval-reason', userId, dayIndex, slot)
  ];

  return {
    id: `demo-approval-${userId}-${day.toISODate()}-${slot}`,
    start: start.toUTC().toISO(),
    end: end.toUTC().toISO(),
    time: durationHours * 3600,
    createdAt: createdAt.toUTC().toISO(),
    approved: status,
    taskId: task.id,
    taskName: task.name,
    projectId: project.id,
    projectName: project.name,
    reason,
    userId,
    userName,
    reviewerId: status === 'pending' ? '' : REVIEWER.id,
    reviewerName: status === 'pending' ? undefined : REVIEWER.name,
  };
}

/** Manual-time approval rows for the Approvals page sample-data path. */
export function buildMockApprovals(
  seed: DemoSeedService,
  options: BuildMockApprovalsOptions = {},
): Approval[] {
  const users = filterDemoReportUsers(buildDemoReportUsers(), {
    user: options.user,
    tag: options.tag,
  }).filter(user => !user.archived);

  const { fromDay, lastDay, zone } = resolveDateRange(options);
  const today = DateTime.now().setZone(zone).startOf('day');
  const approvals: Approval[] = [];

  users.forEach(user => {
    eachWeekday(fromDay, lastDay, (day, dayIndex) => {
      if (day.startOf('day') > today) {
        return;
      }
      // Roughly every other weekday gets a request; denser for a few users.
      const userNum = Number(/(\d+)$/.exec(user.id)?.[1] || 1);
      const include = seed.int(0, 99, 'approval-include', user.id, dayIndex) < (35 + (userNum % 3) * 10);
      if (!include) {
        return;
      }

      const slots = 1 + (seed.int(0, 99, 'approval-slots', user.id, dayIndex) < 20 ? 1 : 0);
      for (let slot = 0; slot < slots; slot += 1) {
        const approval = buildManualApproval(seed, user.id, user.name, day, dayIndex, slot);
        if (matchesApprovedFilter(approval.approved, options.approved)) {
          approvals.push(approval);
        }
      }
    });
  });

  return sortByApiParam(approvals, options['sort-user'] || '_createdAt');
}

function parseLeaveTypes(leaveTypeParam?: string): LeaveType[] {
  if (!leaveTypeParam) {
    return ['paidLeave'];
  }
  return leaveTypeParam
    .split(',')
    .map(value => value.trim())
    .filter((value): value is LeaveType => value === 'paidLeave' || value === 'unpaidLeave');
}

/** Leave-request shifts for Approvals when `leave-type` is present (not Attendance). */
export function buildMockLeaveApprovalShifts(
  seed: DemoSeedService,
  params: GetWorkSchedulesParams & { approved?: string, 'leave-type'?: string, 'sort-user'?: string },
): Shift[] {
  const users = filterDemoReportUsers(buildDemoReportUsers(), {
    user: params.user,
  }).filter(user => !user.archived);
  const leaveTypes = parseLeaveTypes(params['leave-type']);
  const { fromDay, lastDay, zone } = resolveDateRange(params);
  const today = DateTime.now().setZone(zone).startOf('day');
  const shifts: Shift[] = [];

  users.forEach(user => {
    eachWeekday(fromDay, lastDay, (day, dayIndex) => {
      if (day.startOf('day') > today) {
        return;
      }
      const include = seed.int(0, 99, 'leave-include', user.id, dayIndex) < 18;
      if (!include) {
        return;
      }

      const leaveType = leaveTypes[
        seed.pickIndex(leaveTypes.length, 'leave-type', user.id, dayIndex)
      ];
      const status = statusForRoll(seed.int(0, 99, 'leave-status', user.id, dayIndex));
      if (!matchesApprovedFilter(status, params.approved)) {
        return;
      }

      const start = day.set({ hour: 9, minute: 0, second: 0, millisecond: 0 });
      const end = start.plus({ hours: 8 });
      const createdAt = start.minus({ days: 1 + seed.int(0, 3, 'leave-created', user.id, dayIndex) });
      const reason = DEMO_LEAVE_REASONS[
        seed.pickIndex(DEMO_LEAVE_REASONS.length, 'leave-reason', user.id, dayIndex)
      ];

      // Extra Approval fields are consumed when the Approvals list casts shifts.
      shifts.push({
        id: `demo-leave-${user.id}-${day.toISODate()}`,
        userId: user.id,
        start: start.toUTC().toISO(),
        end: end.toUTC().toISO(),
        minimumHours: 8,
        leaveType,
        approved: status,
        userName: user.name,
        time: 8 * 3600,
        createdAt: createdAt.toUTC().toISO(),
        reason,
        projectId: '',
        taskId: '',
        projectName: 'Leave',
        taskName: leaveType === 'paidLeave' ? 'Paid Leave' : 'Unpaid leave',
        reviewerId: status === 'pending' ? '' : REVIEWER.id,
        reviewerName: status === 'pending' ? undefined : REVIEWER.name,
      } as Shift);
    });
  });

  return sortByApiParam(shifts, params['sort-user'] || '_createdAt');
}
